export const AUTH_SECURITY_CUTOVER_VERSION =
  "2026-07-20-verified-credential-reset-v1";

/**
 * Audit note (A8-17, 2026-09-07, re-checked 2026-09-08): nothing outside this
 * file imports this interface by name. It is declared without `export` because
 * it is only ever the target of `hasCurrentAuthSecurityCutover`'s type
 * predicate — callers narrow through it structurally without naming it.
 *
 * The audit expected TypeScript to refuse a non-exported type in an exported
 * declaration. It does not here: this package is consumed as source and every
 * tsconfig in the tree sets `noEmit: true`, so no declaration file is emitted.
 * Re-export it if that ever changes.
 */
interface AuthSecurityCutoverClaims {
  [claim: string]: unknown;
  authSecurityCutover: typeof AUTH_SECURITY_CUTOVER_VERSION;
}

export function hasCurrentAuthSecurityCutover<T>(
  claims: T,
): claims is T & AuthSecurityCutoverClaims {
  return (
    typeof claims === "object" &&
    claims !== null &&
    "authSecurityCutover" in claims &&
    claims.authSecurityCutover === AUTH_SECURITY_CUTOVER_VERSION
  );
}

/**
 * Better Auth's secure-cookie name prefix (`SECURE_COOKIE_PREFIX` in
 * `better-auth/cookies`).
 */
export const BETTER_AUTH_SECURE_COOKIE_PREFIX = "__Secure-";

/**
 * The two cookie names Better Auth's `getSessionCookie` accepts for the session
 * token, in the order it checks them — its default `cookiePrefix`
 * ("better-auth") joined to its default `cookieName` ("session_token") by a dot
 * and then by a dash.
 *
 * Lives here because BOTH sides read it. The Auth Worker gets the real thing
 * from the package; Product cannot — `better-auth/cookies` is ESM-only and
 * Jest's ts-jest pipeline cannot transform it, which is why
 * `apps/app/src/lib/auth/better-auth-http.ts` reimplements the ten-line
 * cookie parse by hand. A cookie rename in a future Better Auth would otherwise
 * break every Product session in production with every Product test green.
 * `apps/auth/src/better-auth-package-pins.test.ts` is what stops that: it
 * drives the REAL `getSessionCookie` with these names and fails if the package
 * stops accepting one.
 */
export const BETTER_AUTH_SESSION_COOKIE_NAMES = [
  "better-auth.session_token",
  "better-auth-session_token",
] as const;

/**
 * The longest password the Auth Worker accepts, in UTF-8 BYTES. bcrypt reads
 * at most 72 bytes, so `apps/auth/src/password.ts` refuses anything longer
 * (`MAX_BCRYPT_PASSWORD_BYTES` is this value), and the app's password forms
 * validate the same limit so a customer never hits it as a server error.
 * Bytes, not characters: an accented letter or symbol can take two to four.
 */
export const MAX_PASSWORD_BYTES = 72;
